import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
  makeStyles,
  createStyles,
  fade,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useRouteMatch } from "react-router-dom";
import { getLocalStore } from "../../../../../../utils/store/localStore";
import { CustomTheme } from "../../../../../../types/customTheme";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_UPDATED,
} from "../../../../../../utils/consts";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Icon } from "@iconify/react";
import { updateWrapperInfo } from "../../../../../../services/menuApp/menuService";
import ButtonCommon from "../../../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../../../utils/enum";
import _ from "lodash";
import axios, { AxiosError } from "axios";
import { useDispatch } from "react-redux";
import { updateDirectoryInStore } from "../../../../../../redux/actions/menu";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      backgroundColor: theme.palette.background.default,
      minWidth: "400px",
      [theme.breakpoints.down("sm")]: {
        minWidth: "300px",
        margin: "16px",
      },
    },
    dialogTitle: {
      borderBottom: `1px solid ${theme.palette.background.entity_border}`,
      padding: "16px 24px",
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    listContainer: {
      maxHeight: "60vh",
      overflowY: "auto",
      padding: "2px",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.palette.background.entity_border,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: theme.palette.grey[600],
        borderRadius: "3px",
      },
    },
    listItem: {
      display: "flex",
      alignItems: "center",
      padding: "12px",
      marginBottom: "8px",
      backgroundColor: theme.palette.background.entity_background,
      border: `1px solid ${theme.palette.background.entity_border}`,
      borderRadius: "8px",
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      userSelect: "none",
      "&:hover": {
        backgroundColor: fade(
          theme.palette.background.entity_highlight_background,
          0.5,
        ),
      },
    },
    dragHandle: {
      cursor: "grab",
      display: "flex",
      alignItems: "center",
      marginRight: "16px",
      color: theme.palette.grey[500],
      "&:active": {
        cursor: "grabbing",
      },
    },
    itemImage: {
      width: "40px",
      height: "40px",
      borderRadius: "6px",
      objectFit: "cover",
      marginRight: "12px",
      backgroundColor: theme.palette.background.imgBackground,
      flexShrink: 0,
    },
    itemInfo: {
      flex: 1,
      minWidth: 0,
    },
    itemName: {
      fontSize: "14px",
      fontWeight: "bold",
      marginBottom: "4px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    itemPrice: {
      fontSize: "12px",
      color: theme.palette.custom.yellow.main,
    },
    positionNumber: {
      fontSize: "12px",
      color: theme.palette.grey[500],
      marginLeft: "12px",
      flexShrink: 0,
    },
    dialogActions: {
      padding: "16px 24px",
      borderTop: `1px solid ${theme.palette.background.entity_border}`,
    },
  }),
);

interface ReorderModalProps {
  open: boolean;
  onClose: (success: boolean, orderedItems?: any[]) => void;
  items: any[];
  category: any;
  menuId: string;
  categoryId: string;
  onSave: (updatedItems: any[]) => void;
  setSuccess: (message: string) => void;
  setError: (message: string) => void;
  getProductWrapperInfo: any;
  menuNode: any;
  categoryNode: any;
  productVersion: any;
  onReorderComplete?: () => void;
}

// Helper function to fetch single wrapper info
const fetchSingleWrapperInfo = async (
  locationId: string,
  wrapperId: number,
) => {
  const response = await axios.get(
    `${API_BASE_URL}/locations/${locationId}/wrappers/${wrapperId}`,
  );
  return response;
};

const ReorderModal: React.FC<ReorderModalProps> = ({
  open,
  onClose,
  items,
  category,
  menuId,
  categoryId,
  onSave,
  setSuccess,
  setError,
  getProductWrapperInfo,
  menuNode,
  categoryNode,
  productVersion,
  onReorderComplete,
}) => {
  const classes = useStyles();
  const theme = useTheme<CustomTheme>();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [orderedItems, setOrderedItems] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const match: any = useRouteMatch();
  const dispatch = useDispatch();

  useEffect(() => {
    if (items && items.length > 0) {
      const sorted = [...items].sort(
        (a, b) => (a.viewOrder || 0) - (b.viewOrder || 0),
      );
      setOrderedItems(sorted);
    }
  }, [items]);

  const handleDragEnd = useCallback((result: any) => {
    if (!result.destination) return;

    if (result.source.index === result.destination.index) return;

    setOrderedItems((prevItems) => {
      const reordered = [...prevItems];
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, removed);
      return reordered;
    });
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Prepare items with new viewOrder
      const itemsWithNewOrder = orderedItems.map((item, index) => ({
        ...item,
        viewOrder: index,
      }));

      // Find which items actually changed
      const itemsToUpdate = itemsWithNewOrder.filter(
        (item, index) =>
          item.viewOrder !==
          items.find((i: any) => i.id === item.id)?.viewOrder,
      );

      if (itemsToUpdate.length === 0) {
        setSuccess(SUCCESSFULLY_UPDATED);
        onSave(itemsWithNewOrder);
        onClose(true, itemsWithNewOrder);
        setIsSaving(false);
        if (onReorderComplete) {
          onReorderComplete();
        }
        return;
      }

      // Store updated items with new versions from responses
      const updatedItemsMap = new Map();
      const updatedItemsForRedux: any[] = [];

      // Update each item and collect the updated data with new versions
      for (let i = 0; i < itemsToUpdate.length; i++) {
        const item = itemsToUpdate[i];

        // Get the current version from the original items array
        const originalItem = items.find((i: any) => i.id === item.id);
        const currentVersion = originalItem?.version || item.version;

        const body = {
          locationId: match.params.locationId,
          viewOrder: item.viewOrder,
          id: item.id,
          version: currentVersion,
        };

        try {
          const res = await updateWrapperInfo(
            match.params.locationId,
            body,
            "Menus Changed",
            `Reorder item ${item.name} to position ${item.viewOrder}`,
            "menu",
          );

          // If response has updated data, store it with new version
          if (res.data && res.data.data) {
            const updatedItemData = {
              ...item,
              ...res.data.data,
              version: res.data.data.version,
            };
            updatedItemsMap.set(item.id, updatedItemData);
            updatedItemsForRedux.push(updatedItemData);
          } else {
            updatedItemsMap.set(item.id, item);
            updatedItemsForRedux.push(item);
          }
        } catch (error) {
          const err: any = error as AxiosError;
          console.error(`Error updating item ${item.id}:`, err);

          // Handle 409 conflict error - get fresh version and retry
          if (err?.response?.status === 409) {
            try {
              // Fetch fresh item data
              const freshItemRes = await fetchSingleWrapperInfo(
                match.params.locationId,
                item.id,
              );

              if (freshItemRes.data && freshItemRes.data.data) {
                const freshItem = freshItemRes.data.data;
                const retryBody = {
                  locationId: match.params.locationId,
                  viewOrder: item.viewOrder,
                  id: item.id,
                  version: freshItem.version,
                };

                const retryRes = await updateWrapperInfo(
                  match.params.locationId,
                  retryBody,
                  "Menus Changed",
                  `Reorder item ${item.name} to position ${item.viewOrder}`,
                  "menu",
                );

                if (retryRes.data && retryRes.data.data) {
                  const updatedItemData = {
                    ...item,
                    ...retryRes.data.data,
                    version: retryRes.data.data.version,
                  };
                  updatedItemsMap.set(item.id, updatedItemData);
                  updatedItemsForRedux.push(updatedItemData);
                }
              }
            } catch (retryErr) {
              console.error(`Retry failed for item ${item.id}:`, retryErr);
              updatedItemsMap.set(item.id, item);
              updatedItemsForRedux.push(item);
            }
          } else {
            updatedItemsMap.set(item.id, item);
            updatedItemsForRedux.push(item);
          }
        }
      }

      // Build the final ordered items list with updated versions
      const finalOrderedItems = itemsWithNewOrder.map((item) => {
        const updatedItem = updatedItemsMap.get(item.id);
        return updatedItem || item;
      });

      // Update Redux store for each updated item
      updatedItemsForRedux.forEach((updatedItem) => {
        const updatedDataForRedux = {
          id: updatedItem.id,
          valid: updatedItem.valid,
          version: updatedItem.version,
          name: updatedItem.name,
          colour: updatedItem.colour,
          img: updatedItem.img,
          desc: updatedItem.desc,
          directory: updatedItem.directory,
          directoryId: updatedItem.directoryId,
          directoryLevel: updatedItem.directoryLevel,
          itemType: updatedItem.itemType,
          allYouCanEat: updatedItem.allYouCanEat,
          kiosk: updatedItem.kiosk,
          cover: updatedItem.cover,
          nameShort: updatedItem.nameShort,
          viewOrder: updatedItem.viewOrder,
        };
        dispatch(updateDirectoryInStore(updatedDataForRedux));
      });

      setSuccess(SUCCESSFULLY_UPDATED);
      onSave(finalOrderedItems);
      onClose(true, finalOrderedItems);

      // Call the callback to refresh menu details
      if (onReorderComplete) {
        onReorderComplete();
      }
    } catch (err) {
      console.error("Error saving reorder:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      onClose(false);
    } finally {
      setIsSaving(false);
    }
  };

  const getItemImage = (item: any) => {
    if (item.img) return item.img;
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" style={{ fontWeight: "bold" }}>
          Reorder Items - {category?.name || "Category"}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={() => onClose(false)}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: "24px" }}>
        <Typography
          variant="body2"
          style={{ marginBottom: "16px", color: theme.palette.text.secondary }}
        >
          Drag and drop items to reorder them
        </Typography>

        <div className={classes.listContainer}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="items">
              {(provided: any, snapshot: any) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{
                    minHeight: orderedItems.length > 0 ? "auto" : "100px",
                  }}
                >
                  {orderedItems.map((item, index) => (
                    <Draggable
                      key={item.id.toString()}
                      draggableId={item.id.toString()}
                      index={index}
                      disableInteractiveElementBlocking={false}
                    >
                      {(provided: any, snapshot: any) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={classes.listItem}
                          style={{
                            ...provided.draggableProps.style,
                            backgroundColor: snapshot.isDragging
                              ? theme.palette.background
                                  .entity_highlight_background
                              : theme.palette.background.entity_background,
                          }}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className={classes.dragHandle}
                          >
                            <Icon
                              icon="mdi:drag-vertical"
                              width={24}
                              height={24}
                            />
                          </div>

                          {getItemImage(item) ? (
                            <img
                              src={getItemImage(item)}
                              alt={item.name}
                              className={classes.itemImage}
                              draggable={false}
                            />
                          ) : (
                            <Box
                              className={classes.itemImage}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Icon icon="mdi:food" width={24} height={24} />
                            </Box>
                          )}

                          <div className={classes.itemInfo}>
                            <Typography className={classes.itemName}>
                              {item.name}
                            </Typography>
                            {item.price && (
                              <Typography className={classes.itemPrice}>
                                {getLocalStore("currency")}
                                {item.price}
                              </Typography>
                            )}
                          </div>

                          <Typography className={classes.positionNumber}>
                            #{index + 1}
                          </Typography>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {orderedItems.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography style={{ color: theme.palette.text.secondary }}>
                No items to reorder
              </Typography>
            </Box>
          )}
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <ButtonCommon
          onClick={() => onClose(false)}
          style={{ fontSize: 11, width: "100px", marginRight: "4px" }}
          color={buttonColors.CANCEL_BUTTON_COLOR}
        >
          Cancel
        </ButtonCommon>
        <ButtonCommon
          onClick={handleSave}
          color={buttonColors.UPDATE_BUTTON_COLOR}
          style={{ fontSize: 11, width: "120px" }}
          disabled={isSaving || orderedItems.length === 0}
        >
          {isSaving ? <CircularProgress size={24} /> : "Save Order"}
        </ButtonCommon>
      </DialogActions>
    </Dialog>
  );
};

export default ReorderModal;
